import { ExEmptyState, ExLoader, ExTab, ExTabItem } from '@boomi/exosphere';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import './task-list.css';
import Markdown from 'react-markdown';
import DateWithTooltip from '../DateWithTooltip';
import type { ApprovalTaskStatusWithAny, ListTask } from '../sources';
import type { TabType } from '../TaskDataManager';
import TaskStatusBadge from '../TaskStatusBadge';
import FilterPopup from './FilterPopup';

const DELAYED_LOADER_TIMEOUT = 500;

const MarkdownText = ({ content }: { content: string }) => {
    const [textContent, setTextContent] = useState('');
    const markdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTextContent(markdownRef?.current?.innerText || '');
    }, []);

    return (
        <>
            {textContent || (
                <div ref={markdownRef} className="hidden-markdown-text">
                    <Markdown>{content}</Markdown>
                </div>
            )}
        </>
    );
};

interface Props {
    inboxTasks: ListTask[];
    isLoadingInbox: boolean;
    isMoreToLoad: boolean;
    getNextInboxTasksPage: () => Promise<void>;
    loadSelectedTask: (id: string | null) => void;
    selectedTaskId?: string | undefined;
    totalCount: number | undefined;
    currentTab: TabType;
    currentStatus: ApprovalTaskStatusWithAny;
    updateFilters: (tab?: TabType, status?: ApprovalTaskStatusWithAny) => void;
}

const TaskList = ({
    inboxTasks,
    isLoadingInbox,
    isMoreToLoad,
    getNextInboxTasksPage,
    loadSelectedTask,
    selectedTaskId,
    totalCount,
    currentTab,
    currentStatus,
    updateFilters,
}: Props) => {
    const filters = [
        {
            name: 'Assigned to me',
            tab: 'assigned',
            onFilter: () => updateFilters('assigned'),
        },
        {
            name: 'Watching',
            tab: 'watching',
            onFilter: () => updateFilters('watching'),
        },
    ];

    const selectedFilter = filters.find((filter) => filter.tab === currentTab) || filters[0];
    const loaderRef = useRef<HTMLDivElement>(null);

    const loaderTimeoutRef = useRef<number | null>(null);

    const [showDelayedLoader, setShowDelayedLoader] = useState(false);

    // Handle delayed loader
    useEffect(() => {
        if (isLoadingInbox) {
            loaderTimeoutRef.current = window.setTimeout(() => {
                setShowDelayedLoader(true);
            }, DELAYED_LOADER_TIMEOUT);
        } else {
            clearTimeout(loaderTimeoutRef.current ?? undefined);
            setShowDelayedLoader(false);
        }

        return () => {
            clearTimeout(loaderTimeoutRef.current ?? undefined);
        };
    }, [isLoadingInbox]);

    useEffect(() => {
        if (!loaderRef.current || !isMoreToLoad) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                getNextInboxTasksPage();
            }
        });

        observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [isMoreToLoad, getNextInboxTasksPage]);

    return (
        <>
            <div className="tab-container">
                <ExTab
                    onSelect={(e) => {
                        const filter =
                            filters[
                                (
                                    e.detail as unknown as {
                                        selectedIndex: number;
                                    }
                                ).selectedIndex
                            ];
                        filter.onFilter();
                    }}
                    role="tablist"
                >
                    {filters.map(({ name }) => (
                        <ExTabItem key={name} selected={selectedFilter.name === name}>
                            {name}
                        </ExTabItem>
                    ))}
                </ExTab>
                <FilterPopup
                    appliedStatus={currentStatus}
                    setAppliedStatus={(status) => updateFilters(undefined, status)}
                />
            </div>
            <div className="task-list" data-testid="task-list">
                {showDelayedLoader ? (
                    <ExLoader />
                ) : inboxTasks.length > 0 ? (
                    inboxTasks.map(
                        ({ id, title, content, updatedAt, status, isNewlyAdded, isRemoving }) => (
                            <button
                                key={id}
                                className={classNames('task', {
                                    selected: selectedTaskId === id,
                                    'task-enter': isNewlyAdded,
                                    'task-exit': isRemoving,
                                })}
                                onClick={() => loadSelectedTask(id)}
                                onKeyDown={({ key }) => {
                                    if (key !== 'Enter') {
                                        return;
                                    }
                                    loadSelectedTask(id);
                                }}
                                tabIndex={0}
                                type="button"
                            >
                                <div className="inner-task">
                                    <div className="task-header">
                                        <div className="task-title">{title}</div>
                                        <DateWithTooltip dateString={updatedAt} />
                                    </div>
                                    <div className="task-description">
                                        <MarkdownText content={content} />
                                    </div>
                                    <div className="task-badge-row">
                                        <TaskStatusBadge status={status} />
                                    </div>
                                </div>
                            </button>
                        ),
                    )
                ) : !isLoadingInbox ? (
                    <ExEmptyState
                        label="No tasks found"
                        text="There are no tasks matching your current filter criteria."
                    />
                ) : null}
                {isMoreToLoad && !showDelayedLoader ? (
                    <div ref={loaderRef}>
                        <ExLoader />
                    </div>
                ) : null}
                {!isLoadingInbox && (
                    <ExEmptyState
                        label={''}
                        text={`Showing ${inboxTasks.length} of ${
                            totalCount ?? inboxTasks.length
                        } results`}
                    />
                )}
            </div>
        </>
    );
};

export default TaskList;
